import React from "react"
import { useRouteMatch, Route, Switch, Redirect } from "react-router-dom"

import DashboardPage from "pages/dashboard/dashboard"
import UserEdit from "pages/dashboard/user-edit"

import DoctorMessaging from "pages/dashboard/doctor-messaging"

import { useAssertValidRootQuery } from "graphql/smart-queries/user"
import { maybeGetActiveJourney } from "shared/user/helpers"
import { JourneyKindEnum, NextActionEnum, StatusEnum } from "typings/types"

import { lazy } from "shared/lazy"

const TreatmentPlan = lazy(() => import("pages/dashboard/treatment-plan"))
const ApproveTreatmentPlan = lazy(() => import("pages/dashboard/treatment-plan/confirm"))
const AddTreatment = lazy(() => import("pages/dashboard/add-treatment"))
const AddTreatmentCheckout = lazy(() => import("pages/dashboard/add-treatment/checkout"))
const AddTreatmentThankYou = lazy(() => import("pages/dashboard/add-treatment/thank-you"))
const CreateMigraineEvent = lazy(() => import("pages/dashboard/migraine-events/new"))
const UpdateMigraineEvent = lazy(() => import("pages/dashboard/migraine-events/id"))
const ProgressTracking = lazy(() => import("pages/dashboard/progress-tracking"))
const TreatmentPlanEditBillingDate = lazy(() => import("pages/dashboard/treatment-plan/edit-billing-date"))

/**
 * Authorization for this component is handled in pages/index.tsx
 * Any /dashboard/* route is only accessible by a logged in user
 */
function Dashboard() {
  const match = useRouteMatch()
  const { user } = useAssertValidRootQuery()
  const activeJourney = maybeGetActiveJourney(user)

  const doctorExists = !!user.consultation?.doctor
  const consultationCompleted = user.consultation?.status === StatusEnum.Completed
  const hasActiveUtpJourneyBeforeCheckout =
    activeJourney?.kind === JourneyKindEnum.CoveUpdateTreatment &&
    activeJourney.nextActions.includes(NextActionEnum.Checkout)
  const completedOnboardingJourney = (consultationCompleted || user.eCommerceUser) && !activeJourney

  const updateTreatmentJourneyAvailable = completedOnboardingJourney || hasActiveUtpJourneyBeforeCheckout

  return (
    <Switch>
      <Route exact path={`${match.url}`} component={DashboardPage} />
      <Route exact path={`${match.url}/user-edit`} component={UserEdit} />
      <Route exact path={`${match.url}/treatment-plan`} component={TreatmentPlan} />
      <Route exact path={`${match.url}/treatment-plan/confirm`} component={ApproveTreatmentPlan} />
      <Route
        path={`${match.url}/treatment-plan/edit-billing-date/:subscriptionId`}
        component={TreatmentPlanEditBillingDate}
      />
      {doctorExists && <Route exact path={`${match.url}/doctor-messaging`} component={DoctorMessaging} />}

      {updateTreatmentJourneyAvailable && <Route exact path={`${match.url}/add-treatment`} component={AddTreatment} />}
      {updateTreatmentJourneyAvailable && (
        <Route path={`${match.url}/add-treatment/checkout`} component={AddTreatmentCheckout} />
      )}
      <Route exact path={`${match.url}/add-treatment/thank-you`} component={AddTreatmentThankYou} />

      <Route exact path={`${match.url}/migraine-events/new`} component={CreateMigraineEvent} />
      <Route exact path={`${match.url}/migraine-events/:id`} component={UpdateMigraineEvent} />
      <Route exact path={`${match.url}/progress-tracking`} component={ProgressTracking} />
      <Redirect to={match.url} />
    </Switch>
  )
}

export default Dashboard
